// src/pages/Home.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import GeometricDivider from '../components/GeometricDivider';
import ProjectCard from '../components/ProjectCard';
import useDocumentTitle from '../hooks/useDocumentTitle';
import './Home.css';
import { projects } from '../data/projects';
import { skillCategories } from '../data/skills';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const quickFacts = [
  { icon: '🎓', label: 'Bachelor of Computing, University of Guelph' },
  { icon: '💻', label: 'Software Development' },
  { icon: '☁️', label: 'Cloud & DevOps' },
];

export default function Home() {
  useDocumentTitle(
    null,
    'Arian Kazemi — Software engineer and developer.'
  );
  const featured = projects.filter((p) => p.featured);

  return (
    <>
      {/* ——— Hero ——— */}
      <section className="hero">
        <div className="hero-content page-container">
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp}>
            Hi, I’m <span className="highlight">Arian Kazemi</span>
          </motion.h1>
          <motion.p
            className="tagline"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.1 }}
          >
            Software engineer and developer. I love building things — right now that's
            mostly React and Node.js, but the interest is in the craft, not one stack.
          </motion.p>
          <motion.div
            className="hero-buttons"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.2 }}
          >
            <Link to="/projects" className="btn primary">View Projects</Link>
            <Link to="/contact" className="btn secondary">Get In Touch</Link>
          </motion.div>

          <motion.ul
            className="quick-facts"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.3 }}
          >
            {quickFacts.map((fact) => (
              <li key={fact.label}>
                <span aria-hidden="true">{fact.icon}</span> {fact.label}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      <GeometricDivider />

      {/* ——— Featured Projects ——— */}
      <motion.section
        className="home-featured page-container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <h2 className="section-title">Featured Projects</h2>
        <p className="section-subtitle">A closer look at some of my recent work.</p>

        <div className="featured-grid">
          {featured.map((p, i) => (
            <ProjectCard key={p.slug} project={p} index={i} />
          ))}
        </div>

        <div className="view-all">
          <Link to="/projects" className="btn secondary">View All Projects</Link>
        </div>
      </motion.section>

      <GeometricDivider bin1="11001010" bin2="00101101" />

      {/* ——— Skills preview ——— */}
      <motion.section
        className="home-skills page-container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <h2 className="section-title">Skills</h2>
        <p className="section-subtitle">The languages, frameworks, and tools I work with most.</p>

        <div className="skills-preview-grid">
          {skillCategories.slice(0, 4).map((cat) => (
            <div key={cat.title} className="skills-preview-card card">
              <h3>{cat.title}</h3>
              <div className="skills-preview-tags">
                {cat.skills.slice(0, 4).map((s) => (
                  <span key={s.name} className="tech-tag">{s.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="view-all">
          <Link to="/skills" className="btn secondary">See Full Skill Set</Link>
        </div>
      </motion.section>

      <GeometricDivider />
    </>
  );
}
